import {
  createColumnHelper,
  type CellContext,
} from '@standardsdigital/ui-components/Table';
import type { ReactNode } from 'react';
import type {
  ConfigurationEntryDto,
  CustomSubscriptionConfigurationEntryType,
  UpdateCustomSubscriptionConfigurationEntryRequest,
} from '@/redux/services/generate/configurations/customSubscriptionConfigurations';

const createEmptyEntry = (
  entryType: CustomSubscriptionConfigurationEntryType,
): ConfigurationEntryDto => ({
  id: '',
  from: 0,
  to: 0,
  pricePerUnit: 0,
  type: entryType,
});

export const emptyUpdateCustomSubscriptionConfigurationEntryRequest = {
  from: 0,
  to: 0,
  pricePerUnit: 0,
} satisfies UpdateCustomSubscriptionConfigurationEntryRequest;

export const createEntryRequest = (
  values: UpdateCustomSubscriptionConfigurationEntryRequest,
): UpdateCustomSubscriptionConfigurationEntryRequest => ({
  from: values.from,
  to: values.to,
  pricePerUnit: values.pricePerUnit,
});

export const getEntriesToCompare = ({
  configurationEntries,
  editedRow,
  isCreatingEntry,
}: {
  configurationEntries: ConfigurationEntryDto[];
  editedRow: number | undefined;
  isCreatingEntry: boolean;
}): ConfigurationEntryDto[] =>
  configurationEntries.filter((_, index) => isCreatingEntry || index !== editedRow);

export const getPricePerUnitLabelKey = (
  entryType: CustomSubscriptionConfigurationEntryType,
):
  | 'customSubscriptionConfiguration.columns.pricePerSlot'
  | 'customSubscriptionConfiguration.columns.pricePerSeat' =>
  entryType === 'Size'
    ? 'customSubscriptionConfiguration.columns.pricePerSlot'
    : 'customSubscriptionConfiguration.columns.pricePerSeat';

export const getActionsCellState = ({
  editedRow,
  rowIndex,
  isCreatingEntry,
}: {
  editedRow: number | undefined;
  rowIndex: number;
  isCreatingEntry: boolean;
}): {
  isEditingCurrentRow: boolean;
  isAnotherRowEditing: boolean;
  isUnsavedRow: boolean;
} => {
  const isEditingCurrentRow = editedRow === rowIndex;

  return {
    isEditingCurrentRow,
    isAnotherRowEditing: editedRow !== undefined && !isEditingCurrentRow,
    isUnsavedRow: isCreatingEntry && isEditingCurrentRow,
  };
};

const columnHelper = createColumnHelper<ConfigurationEntryDto>();

export const createColumns = ({
  fromLabel,
  toLabel,
  pricePerUnitLabel,
  actionsLabel,
  renderEditableNumberCell,
  renderActionsCell,
}: {
  fromLabel: string;
  toLabel: string;
  pricePerUnitLabel: string;
  actionsLabel: string;
  renderEditableNumberCell: (
    cell: CellContext<ConfigurationEntryDto, number>,
    name: keyof UpdateCustomSubscriptionConfigurationEntryRequest,
    label: string,
  ) => ReactNode;
  renderActionsCell: (
    cell: CellContext<ConfigurationEntryDto, unknown>,
  ) => ReactNode;
}) => [
  columnHelper.accessor('from', {
    header: fromLabel,
    meta: {
      columnSize: '33%',
    },
    cell: (cell) => renderEditableNumberCell(cell, 'from', fromLabel),
  }),
  columnHelper.accessor('to', {
    header: toLabel,
    meta: {
      columnSize: '33%',
    },
    cell: (cell) => renderEditableNumberCell(cell, 'to', toLabel),
  }),
  columnHelper.accessor('pricePerUnit', {
    header: pricePerUnitLabel,
    meta: {
      columnSize: '33%',
    },
    cell: (cell) =>
      renderEditableNumberCell(cell, 'pricePerUnit', pricePerUnitLabel),
  }),
  columnHelper.display({
    id: 'actions',
    header: actionsLabel,
    minSize: 184,
    cell: renderActionsCell,
  }),
];

export const getRows = ({
  configurationEntries,
  editedEntryValues,
  editedRow,
  entryType,
  isCreatingEntry,
}: {
  configurationEntries: ConfigurationEntryDto[];
  editedEntryValues: UpdateCustomSubscriptionConfigurationEntryRequest;
  editedRow: number | undefined;
  entryType: CustomSubscriptionConfigurationEntryType;
  isCreatingEntry: boolean;
}): ConfigurationEntryDto[] => {
  if (editedRow === undefined) {
    return configurationEntries;
  }

  const editedRowData = isCreatingEntry
    ? createEmptyEntry(entryType)
    : configurationEntries[editedRow];

  if (editedRowData === undefined) {
    return configurationEntries;
  }

  const editedEntry: ConfigurationEntryDto = {
    ...editedRowData,
    ...createEntryRequest(editedEntryValues),
  };

  if (isCreatingEntry) {
    return [...configurationEntries, editedEntry];
  }

  return configurationEntries.map((entry, index) =>
    index === editedRow ? editedEntry : entry,
  );
};
